import { Badge, Button, Group, Tooltip } from "@mantine/core";
import { IChecklistItemRow, IChecklistPopulated, TChecklistItemStatus } from "@mctypes/checklist.types";
import { IconPlus } from "@tabler/icons-react";
import { ColDef, ICellRendererParams, NewValueParams, ValueSetterParams } from "ag-grid-community";
import React from "react";
import { Checkbox, ConfirmDialog } from "../../../../components";
import { LinkifiedText } from "../../../../components/LinkifiedText";
import StampCheck from "../StampCheck";

export const DEFAULT_CHECKLIST_COL_DEF: ColDef<IChecklistItemRow> = {
  editable: false,
  resizable: true,
  sortable: false,
  suppressHeaderMenuButton: true,
  cellStyle: {
    whiteSpace: "normal",
    lineHeight: "1.5",
    wordBreak: "break-word",
  },
};

type ChecklistColumnOptions = {
  allCategoriesCollapsed: boolean;
  collapsedCategoryIds: Set<string>;
  editMode: boolean;
  onAddItem: (categoryId: string) => void;
  onDeleteCategory: (categoryId: string) => void;
  onDeleteItem: (itemId: string) => void;
  onOpenRow: (row: IChecklistItemRow) => void;
  onToggleAllCategories: () => void;
  onToggleCategory: (categoryId: string) => void;
  onToggleInactive: (row: IChecklistItemRow) => void;
  onUpdateCategory: (categoryId: string, attribute: string, oldValue: unknown, newValue: unknown) => void;
  onUpdateItem: (itemId: string, path: string, oldValue: unknown, newValue: unknown) => void;
};

export const getChecklistColumns = (
  checklist: IChecklistPopulated,
  activeCheckKeys: string[],
  hiddenColumns: string[],
  options: ChecklistColumnOptions,
): ColDef<IChecklistItemRow>[] => {
  const checkColumns: ColDef<IChecklistItemRow>[] = activeCheckKeys.map(
    (checkKey) =>
      ({
        colId: `checks.${checkKey}.status`,
        headerName: checklist.checkTypes?.[checkKey]?.name ?? checkKey,
        width: 190,
        minWidth: 150,
        cellClass: "grid-display-block",
        valueGetter: (params) => {
          if (params.data?.originalItem == null) return "";
          return params.data?.checks?.[checkKey]?.status ?? "-";
        },
        cellRenderer: (params) => <CheckStatusCell {...params} checklist={checklist} checkKey={checkKey} />,
        onUpdateItem: options.onUpdateItem,
        hide:
          hiddenColumns.includes(`checks.${checkKey}.status`) ||
          hiddenColumns.includes(checkKey) ||
          (options.editMode === true && checklist.includeIrOnDefaultRoles !== true),
      } as ColDef<IChecklistItemRow> & { onUpdateItem: ChecklistColumnOptions["onUpdateItem"] }),
  );

  return [
    {
      colId: "expand",
      suppressColumnsToolPanel: true,
      pinned: "left",
      lockPinned: true,
      lockPosition: "left",
      suppressMovable: true,
      headerName: "",
      maxWidth: 52,
      width: 52,
      headerClass: "p-0",
      cellClass: "justify-content-center px-0",
      headerComponent: () => (
        <div
          onClick={options.onToggleAllCategories}
          className={`expandIcon${options.allCategoriesCollapsed === false ? " rotateIcon" : ""}`}
        >
          <i className="fa fa-angle-up" />
        </div>
      ),
      cellRenderer: (params: ICellRendererParams<IChecklistItemRow>) => {
        const row = params.data;
        if (row?.originalItem != null) return null;

        return (
          <div
            onClick={() => options.onToggleCategory(row._id)}
            className={`expandIcon${options.collapsedCategoryIds.has(row._id) === true ? " rotateIcon" : ""}`}
          >
            <i className="fa fa-angle-up" />
          </div>
        );
      },
    },
    {
      colId: "edit-actions",
      hide: options.editMode === false,
      suppressColumnsToolPanel: true,
      suppressMovable: true,
      headerName: "",
      width: 100,
      resizable: false,
      cellRenderer: (params: ICellRendererParams<IChecklistItemRow>) => {
        const row = params.data;
        if (row == null) return null;

        if (row.originalItem != null) {
          return (
            <ConfirmDialog
              confirmCb={() => options.onDeleteItem(row._id)}
              title="Slett rad"
              message="Er du sikker på at du vil slette denne raden?"
            >
              <Button className="mr-2" size="compact-sm" variant="light" color="red">
                <i className="fa fa-trash fa-fw" />
              </Button>
            </ConfirmDialog>
          );
        }

        return (
          <div className="d-flex">
            <ConfirmDialog
              confirmCb={() => options.onDeleteCategory(row._id)}
              title="Slett kategori"
              message="Er du sikker på at du vil slette denne kategorien og alle tilhørende rader?"
            >
              <Button className="mr-2" size="compact-sm" variant="light" color="red">
                <i className="fa fa-trash fa-fw" />
              </Button>
            </ConfirmDialog>
            <Button size="compact-sm" variant="light" color="green" onClick={() => options.onAddItem(row._id)}>
              <IconPlus stroke={4} size={16} />
            </Button>
          </div>
        );
      },
    },
    {
      colId: "inactive.isChecked",
      field: "inactive.isChecked",
      headerName: "IR",
      hide: options.editMode === true,
      width: 65,
      filter: true,
      filterParams: {
        suppressMiniFilter: true,
      },
      valueGetter: (params) => (params.data?.inactive?.isChecked === true ? "Ikke relevant" : "Relevant"),
      cellRenderer: (params: ICellRendererParams<IChecklistItemRow>) => {
        const row = params.data;
        if (row?.inactive == null) return null;

        return (
          <div className="d-flex w-100 align-items-center justify-content-center">
            <Checkbox
              icon="ban"
              isChecked={row.inactive.isChecked === true}
              setChecked={() => options.onToggleInactive(row)}
              disabled={false}
              color="var(--mantine-color-mc-primary-6)"
            />
          </div>
        );
      },
    },
    {
      colId: "rowId",
      headerName: "Nr.",
      field: "rowId",
      width: 100,
      minWidth: 80,
      filter: "agTextColumnFilter",
      rowDrag: options.editMode === true,
      hide: options.editMode === false && hiddenColumns.includes("rowId"),
      cellClass: "px-0",
      cellRenderer: (params: ICellRendererParams<IChecklistItemRow>) => (
        <RowIdCell {...params} onOpenRow={options.onOpenRow} />
      ),
    },
    {
      colId: "title",
      headerName: "Kontrollspørsmål (krav)",
      field: "title",
      filter: "agTextColumnFilter",
      minWidth: 380,
      maxWidth: 800,
      flex: 2,
      editable: options.editMode,
      autoHeight: true,
      cellClass: (params) =>
        params.data?.originalItem == null ? "grid-display-block bold-cell" : "grid-display-block",
      cellRenderer: TitleCell,
      cellStyle: { whiteSpace: "break-spaces" },
      cellEditor: options.editMode === true ? "agLargeTextCellEditor" : undefined,
      cellEditorPopup: options.editMode,
      cellEditorParams: {
        maxLength: 5000,
      },
      onCellValueChanged:
        options.editMode === true
          ? (event: NewValueParams<IChecklistItemRow, string>) => {
              const isItem = event.data.originalItem != null;
              const oldValue = (isItem ? event.data.originalItem?.title : event.data.originalCategory?.title) ?? "-";
              const newValue = event.newValue ?? "-";

              if (oldValue === newValue) {
                return;
              }

              if (isItem) {
                options.onUpdateItem(event.data._id, "title", oldValue, newValue);
                return;
              }

              options.onUpdateCategory(event.data._id, "title", oldValue, newValue);
            }
          : undefined,
      hide: options.editMode === false && hiddenColumns.includes("title"),
    },
    {
      colId: "reference",
      headerName: "Referanse",
      field: "reference",
      filter: "agTextColumnFilter",
      width: 150,
      autoHeight: true,
      editable: (params) => params.data?.originalItem != null && options.editMode === true,
      cellEditor: options.editMode === true ? "agLargeTextCellEditor" : undefined,
      cellEditorPopup: options.editMode,
      cellEditorParams: {
        maxLength: 5000,
      },
      onCellValueChanged:
        options.editMode === true
          ? (event: NewValueParams<IChecklistItemRow, string>) => {
              if (event.data.originalItem == null) {
                return;
              }

              const oldValue = event.data.originalItem.reference ?? "";
              const newValue = event.newValue ?? "";

              if (oldValue !== newValue) {
                options.onUpdateItem(event.data._id, "reference", oldValue, newValue);
              }
            }
          : undefined,
      hide: options.editMode === false && hiddenColumns.includes("reference"),
    },
    {
      colId: "dueDate",
      field: "dueDate",
      headerName: "Frist",
      filter: "agDateColumnFilter",
      width: 130,
      minWidth: 130,
      hide: options.editMode === true || hiddenColumns.includes("dueDate"),
      editable: (params) => params.data?.originalItem == null,
      cellEditor: "agDateStringCellEditor",
      valueGetter: (params) => {
        const date = params.data?.dueDate != null ? params.data.dueDate.split("T")[0] : null;
        return date;
      },
      valueSetter: (params: ValueSetterParams<IChecklistItemRow, string | null>) => {
        if (params.data.originalItem != null || params.oldValue === params.newValue) {
          return false;
        }

        options.onUpdateCategory(params.data._id, "dueDate", params.oldValue, params.newValue);
        return true;
      },
    },
    ...checkColumns,
    {
      colId: "comment",
      headerName: "Kommentar",
      field: "comment",
      filter: "agTextColumnFilter",
      minWidth: 220,
      flex: 1,
      cellClass: "p-1 three-line-text-truncate",
      valueGetter: (params) => params.data?.comment ?? "",
      cellRenderer: CommentCell,
      editable: (params) => params.data?.originalItem != null,
      cellEditor: "agLargeTextCellEditor",
      cellEditorPopup: true,
      cellEditorParams: {
        maxLength: 2000,
      },
      onCellValueChanged: (event: NewValueParams<IChecklistItemRow, string>) => {
        if (event.data.originalItem == null || event.oldValue === event.newValue) {
          return;
        }

        options.onUpdateItem(event.data._id, "comment", event.oldValue ?? "", event.newValue ?? "");
      },
      hide: options.editMode === true || hiddenColumns.includes("comment"),
    },
  ];
};

const TitleCell: React.FC<ICellRendererParams<IChecklistItemRow>> = ({ data }) => {
  if (data == null) return null;

  return <LinkifiedText text={data.title} />;
};

const RowIdCell: React.FC<ICellRendererParams<IChecklistItemRow> & { onOpenRow: (row: IChecklistItemRow) => void }> = ({
  data,
  onOpenRow,
}) => {
  if (data == null) return null;

  return (
    <div className="d-flex p-0 m-0 align-items-center">
      <Button variant="subtle" color="mc-primary" px="xs" size="sm" fw={600} onClick={() => onOpenRow(data)}>
        {data.originalItem == null ? (
          <b className={data.inactive?.isChecked === true ? "text-inactive" : ""}>{data.rowId}</b>
        ) : (
          <span className={data.inactive?.isChecked === true ? "text-inactive" : ""}>{data.rowId}</span>
        )}
      </Button>
      <div className="flex-fill" />
    </div>
  );
};

const CommentCell: React.FC<ICellRendererParams<IChecklistItemRow, string>> = ({ value, data }) => {
  if (data?.originalItem == null || value == null || value === "") return null;

  return (
    <Tooltip openDelay={200} p="sm" withArrow label={value} multiline w={240} disabled={value.length <= 100}>
      <div className="three-line-text-truncate">{value}</div>
    </Tooltip>
  );
};

const CheckStatusCell: React.FC<
  ICellRendererParams<IChecklistItemRow> & { checklist: IChecklistPopulated; checkKey: string }
> = ({ data, checklist, checkKey, colDef }) => {
  if (data == null) return null;

  if (data.originalItem == null) {
    const assignedTo = data.originalCategory?.checks?.[checkKey]?.assignedTo;
    const label = typeof assignedTo === "string" ? assignedTo : assignedTo?.name ?? "";

    return label ? (
      <Group h="100%" align="center">
        <Badge variant="light">{label}</Badge>
      </Group>
    ) : null;
  }

  const checkItem = data.checks?.[checkKey];
  if (checkItem == null) return null;

  const checkNumber = Number(checkKey.replace("check", ""));
  const statusDisabled = checkItem.status === "DISABLED";

  return (
    <div className="d-flex align-items-center h-100 grid-cell-center-fix">
      <StampCheck
        templateMode={checklist.isTemplate}
        includeIr={checkNumber > 3 || (checklist.isTemplate === true && statusDisabled !== true)}
        includeDisabled={checklist.isTemplate === true}
        statusDisabled={statusDisabled}
        disabled={false}
        toggle={(rowId: string, oldValue: TChecklistItemStatus, newValue: TChecklistItemStatus) =>
          (
            colDef as ColDef<IChecklistItemRow> & { onUpdateItem?: ChecklistColumnOptions["onUpdateItem"] }
          ).onUpdateItem?.(rowId, `checks.${checkKey}.status`, oldValue, newValue)
        }
        updateCommentText={(text) =>
          (
            colDef as ColDef<IChecklistItemRow> & { onUpdateItem?: ChecklistColumnOptions["onUpdateItem"] }
          ).onUpdateItem?.(data._id, `checks.${checkKey}.comment.text`, "", text)
        }
        checkItem={checkItem}
        row={data}
        stampSettings={checklist.stampSettings}
        includeIrOnDefaultRoles={checklist.includeIrOnDefaultRoles === true && statusDisabled !== true}
        includeUavOnDefaultRoles={checklist.includeUavOnDefaultRoles === true && statusDisabled !== true}
      />
    </div>
  );
};
